/**
 * Shared native AudioContext for the drone audio engine.
 *
 * We create our own native AudioContext (rather than using Tone.js's wrapper)
 * because Tone.js v15 wraps the context in a Proxy that passes property access
 * through but fails the browser's internal brand-check in AudioWorkletNode's
 * constructor.
 *
 * This context is intentionally decoupled from Tone.js so that the metronome
 * can use its own AudioContext, enabling independent audio output routing via
 * AudioContext.setSinkId().
 */

let Tone: typeof import("tone") | null = null;
let nativeCtx: AudioContext | null = null;
let nativeCtxPromise: Promise<AudioContext> | null = null;

async function getTone(): Promise<typeof import("tone")> {
  if (!Tone) {
    Tone = await import("tone");
  }
  return Tone;
}

async function getNativeAudioContext(): Promise<AudioContext> {
  if (nativeCtx) return nativeCtx;
  if (nativeCtxPromise) return nativeCtxPromise;

  nativeCtxPromise = (async () => {
    const ctx = new AudioContext();
    nativeCtx = ctx;
    return ctx;
  })();

  try {
    return await nativeCtxPromise;
  } catch (error) {
    nativeCtx = null;
    throw error;
  } finally {
    nativeCtxPromise = null;
  }
}

export { getTone, getNativeAudioContext };
